import { useCallback, useEffect, useRef, useState } from "react";

import flaskApi from "../../services/flaskApi";

const POLL_INTERVAL_MS = 4000;
const STATUS_QUEUED = "queued";
const STATUS_RUNNING = "running";

const isActive = (job) =>
  job?.status === STATUS_QUEUED || job?.status === STATUS_RUNNING;

const loadCache = (cacheKey) => {
  if (!cacheKey) return null;
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const saveCache = (cacheKey, payload) => {
  if (!cacheKey) return;
  try {
    localStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch {
    // localStorage full / disabled — fine to skip
  }
};

/**
 * Hook that powers paginated job listings for the async tools.
 *
 * Backend contract (see `_paginated_jobs_response` in shopify_routes.py):
 *   GET <endpoint>?page=<n>&page_size=<n> →
 *     { active: [...], finished: [...], total_finished, page, page_size }
 *
 * Active (queued/running) jobs are shown on every page so an in-flight job
 * never gets paginated out of view. Finished jobs are paginated.
 *
 * The hook owns: the current page, the visible jobs, the poll loop, and a
 * small localStorage cache of the most recent page for snappy reloads.
 */
export default function useJobsPagination({
  endpoint,
  cacheKey = "",
  pageSize = 10,
}) {
  const [activeJobs, setActiveJobs] = useState([]);
  const [finishedJobs, setFinishedJobs] = useState([]);
  const [totalFinished, setTotalFinished] = useState(0);
  const [page, setPageState] = useState(1);

  const pageRef = useRef(1);
  const pollTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Hydrate from cache once on mount.
  useEffect(() => {
    const cached = loadCache(cacheKey);
    if (cached) {
      if (Array.isArray(cached.active)) setActiveJobs(cached.active);
      if (Array.isArray(cached.finished)) setFinishedJobs(cached.finished);
      if (typeof cached.total_finished === "number") {
        setTotalFinished(cached.total_finished);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Fetch the page that's currently selected. Returns the response or null on error.
  const fetchPage = useCallback(
    async (targetPage = pageRef.current) => {
      try {
        const response = await flaskApi.get(endpoint, {
          params: { page: targetPage, page_size: pageSize },
        });
        if (!isMountedRef.current) return null;
        const data = response.data || {};
        // Backend response shape evolved from `{jobs: [...]}` (legacy, all
        // statuses in one array, no pagination) to `{active, finished,
        // total_finished, page, page_size}` (current). Tolerate both so the
        // frontend can ship ahead of the backend redeploy without breaking
        // the job list. When the legacy payload arrives, we split it by
        // status client-side and treat the result as a single page.
        let active;
        let finished;
        let total;
        let serverPage;
        if (Array.isArray(data.active) || Array.isArray(data.finished)) {
          active = Array.isArray(data.active) ? data.active : [];
          finished = Array.isArray(data.finished) ? data.finished : [];
          total = Number.isFinite(data.total_finished) ? data.total_finished : 0;
          serverPage = Number.isFinite(data.page) ? data.page : targetPage;
        } else if (Array.isArray(data.jobs)) {
          const isActiveStatus = (s) => s === STATUS_QUEUED || s === STATUS_RUNNING;
          active = data.jobs.filter((j) => isActiveStatus(j?.status));
          finished = data.jobs.filter((j) => !isActiveStatus(j?.status));
          total = finished.length;
          serverPage = 1;
        } else {
          active = [];
          finished = [];
          total = 0;
          serverPage = targetPage;
        }

        setActiveJobs(active);
        setFinishedJobs(finished);
        setTotalFinished(total);
        if (serverPage !== pageRef.current) {
          pageRef.current = serverPage;
          setPageState(serverPage);
        }
        // Cache the most-recent page so reloads paint instantly.
        if (serverPage === 1) {
          saveCache(cacheKey, {
            active,
            finished,
            total_finished: total,
          });
        }
        return { active, finished, total };
      } catch (error) {
        console.error(error);
        // Don't spam toasts on background polls; tool can surface its own first failure if needed.
        return null;
      }
    },
    [cacheKey, endpoint, pageSize]
  );

  const schedulePoll = useCallback(
    (hasActive) => {
      clearPollTimer();
      if (!hasActive) return;
      pollTimerRef.current = setTimeout(async () => {
        const next = await fetchPage();
        schedulePoll(Boolean(next && next.active && next.active.length > 0));
      }, POLL_INTERVAL_MS);
    },
    [clearPollTimer, fetchPage]
  );

  // Initial fetch + start polling if anything is active.
  useEffect(() => {
    isMountedRef.current = true;
    (async () => {
      const initial = await fetchPage();
      if (initial && isMountedRef.current) {
        schedulePoll(initial.active.length > 0);
      }
    })();
    return () => {
      isMountedRef.current = false;
      clearPollTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPage = useCallback(
    async (nextPage) => {
      const target = Math.max(1, Number(nextPage) || 1);
      pageRef.current = target;
      setPageState(target);
      const result = await fetchPage(target);
      if (result) schedulePoll(result.active.length > 0);
    },
    [fetchPage, schedulePoll]
  );

  const refetch = useCallback(async () => {
    const result = await fetchPage();
    if (result) schedulePoll(result.active.length > 0);
    return result;
  }, [fetchPage, schedulePoll]);

  const totalPages = Math.max(1, Math.ceil(totalFinished / pageSize));
  // What the rest of the UI sees: active first, then current page of finished.
  const jobs = [...activeJobs, ...finishedJobs];
  const hasActiveJob = activeJobs.length > 0;

  return {
    activeJobs,
    finishedJobs,
    jobs,
    page,
    pageSize,
    totalFinished,
    totalPages,
    hasActiveJob,
    setPage,
    refetch,
  };
}
